import type { NextPage, GetStaticProps, GetStaticPaths } from 'next'
import Head from 'next/head'
import { Fragment } from 'react'
import Article from '../../components/article/Article'
import Footer from '../../components/layout/Footer'
import Navbar from '../../components/layout/Navbar'
import { resolveImageSrc } from '../../utils/image'
import { isMediaUrl, mediaSrc } from '../../utils/imageLoader'
import { ARTICLE_BODY_WIDTHS, collectArticleImageUrls } from '../../utils/articleHtml'
import { probeImageDimensions } from '../../utils/imageDimensions'
import {
  SITE_NAME,
  SITE_SHARE_IMAGE,
  SITE_URL,
  TWITTER_HANDLE,
  breadcrumbLd,
  publisherLd,
  stripHtmlToText,
  truncate,
} from '../../utils/seo'
import { serverFetch } from '../../utils/serverFetch'
import { slimArticleAuthor, slimArticleGame } from '../../utils/cardPost'
import { fetchBannerAd } from '../../utils/homeData'

// Maps an article's category onto the section listing it belongs to, so the
// breadcrumb Google renders under the result matches the site's real structure.
const CATEGORY_SECTION: Record<string, { name: string; path: string }> = {
  News: { name: 'ข่าวเกม', path: '/news' },
  Review: { name: 'รีวิวเกม', path: '/review' },
  ReviewBanner: { name: 'รีวิวเกม', path: '/review' },
  Feature: { name: 'บทความเกม', path: '/feature' },
  Guide: { name: 'ไกด์เกม', path: '/guide' },
}

function extractFirstImageUrl(contentBody: unknown): string | null {
  if (!contentBody) return null;
  const str = typeof contentBody === 'string' ? contentBody : JSON.stringify(contentBody);
  // Match src in HTML <img> tags or Tiptap JSON "src" attributes
  const match = str.match(/(?:<img[^>]+src="|"src"\s*:\s*")([^"]+\.(?:gif|webp|jpg|jpeg|png))/i);
  return match?.[1] ?? null;
}

// Preload the LCP body image at the same URL the rendered <img> resolves to, so
// the preloaded bytes are the resized version and the preload isn't wasted on a
// raw multi-hundred-KB original — and, more importantly, so preload and <img>
// agree. A mismatch here downloads the image twice.
// GIFs are left raw (the media worker serves them byte-for-byte; a resized GIF is
// a still frame). Non-media hosts fall through to their original URL, matching
// what processArticleImages now leaves them as.
function toOptimizedLcpUrl(rawUrl: string | null): string | null {
  if (!rawUrl) return null;
  if (!/^https?:\/\//i.test(rawUrl)) return null;
  if (/\.gif(\?|#|$)/i.test(rawUrl)) return rawUrl;
  // Normalize (decode %2F Google Storage paths) before matching the media host.
  const normalized = resolveImageSrc(rawUrl) || rawUrl;
  return mediaSrc(normalized, 1080);
}

const ArticlePage: NextPage = (props: any) => {
  const metaData = props.metaResponse
  const articleId = props.articleId
  const lcpImageUrl: string | null = props.lcpImageUrl ?? null
  const ogImageUrl: string = props.ogImageUrl || SITE_SHARE_IMAGE
  const pageTitle: string = props.pageTitle || metaData.title
  const description: string = props.description || metaData.description || ''
  const canonicalUrl = `${SITE_URL}/article/${articleId}`
  const jsonLd: Record<string, any>[] = props.jsonLd ?? []
  return (
    <Fragment>
    <Head>
        <title>{pageTitle}</title>
        <meta
        name='viewport'
        content='width=device-width, initial-scale=1'/>
        <meta name='description' content={description} key="description" />
        {/* max-image-preview:large is what makes articles eligible for the big
            SERP thumbnail and for Google Discover. */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" key="robots" />
        <link rel="canonical" href={canonicalUrl} key="canonical" />

        <meta property="og:type" content="article" key="og:type" />
        <meta property="og:site_name" content={SITE_NAME} key="og:site_name" />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        <meta property="og:locale" content="th_TH" key="og:locale" />
        <meta property="og:title" content={pageTitle} key="title" />
        <meta property="og:description" content={description} key="og:description" />
        <meta property="og:image" content={ogImageUrl} key="image" />
        <meta property="og:image:alt" content={metaData.title} key="og:image:alt" />
        <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
        {/* twitter:site takes the publisher's @handle — it used to hold the
            article URL, which X ignores. */}
        <meta name="twitter:site" content={TWITTER_HANDLE} key="twitter:site" />
        <meta name="twitter:title" content={pageTitle} key="twitter:title" />
        <meta name="twitter:description" content={description} key="twitter:description" />
        <meta name="twitter:image" content={ogImageUrl} key="twitter:image" />
        {/* The first body image is the LCP element on laptops and desktops,
            where it sits in the first viewport. On phones it is below the title,
            description and meta rows, off the first screen, and the LCP is the
            description text — there a 590 KB preload only competes with the CSS,
            fonts and scripts the text needs, so the hint is limited to `md` up. */}
        {lcpImageUrl && (
          <link
            rel="preload"
            as="image"
            href={lcpImageUrl}
            media="(min-width: 768px)"
            {...(props.lcpImageSrcSet
              ? { imageSrcSet: props.lcpImageSrcSet, imageSizes: '(max-width: 768px) 100vw, 800px' }
              : {})}
            fetchPriority="high"
            key="lcp-preload"
          />
        )}
        {jsonLd.map((schema, i) => (
          <script
            key={`jsonld-${i}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        ))}
    </Head>
    <div className="font-sans flex flex-col min-h-screen bg-theme-bg text-theme-text">
      <Navbar />
      <div className="flex-1 mt-20">
      <Article
        articleId={articleId}
        title={metaData.title}
        description={metaData.description}
        gameId={metaData.gameId}
        initialArticle={props.initialArticle}
        bodyImageDimensions={props.bodyImageDimensions}
        initialTopBannerAd={props.topBannerAd}
        initialTopSquareAd={props.topSquareAd}
        initialBottomBannerAd={props.bottomBannerAd}
        className="flex-1"
      />
      </div>
      <Footer />
    </div>
    </Fragment>
  )
}


export default ArticlePage

export const getStaticPaths: GetStaticPaths = async () => {
  // Public, id-keyed content — render on demand and cache via ISR.
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const articleId = context.params?.id as string
  // Garbage ids (robots.txt, .png, null) 404 without a backend round-trip.
  if (!/^[0-9a-f]{24}$/i.test(articleId)) return { notFound: true, revalidate: 30 }

  // The GLOBAL banners, started alongside the content fetch. A backend banner
  // replaces the Google slot; `null` (none, or a failed fetch) keeps Google.
  const bannerAdsPromise = Promise.all([
    fetchBannerAd('GLOBAL', 'TOP'),
    fetchBannerAd('GLOBAL', 'TOP', 'SECOND'),
    fetchBannerAd('GLOBAL', 'BOTTOM'),
  ])

  try {
    const contentRes = await serverFetch(`${process.env.BACKEND_BASE_URL}/contents/${articleId}`)
    const contentData = await contentRes.json()

    // Only a genuine 404 becomes notFound (OpenNext deletes the R2 key on
    // notFound); any other failure throws so the stale ISR copy keeps serving.
    if (
      contentRes.status === 404 ||
      contentData?.statusCode === 404 ||
      contentData?.data?.statusCode === 404
    ) {
      return { notFound: true, revalidate: 30 }
    }
    if (!contentRes.ok || !contentData?.success) {
      throw new Error(`GET /contents/${articleId} failed: HTTP ${contentRes.status}`)
    }

    const content = contentData.data
    // Same shape /contents/meta used to return, derived from the content itself
    // (one backend call instead of two). gameId must be a string: undefined is
    // not serializable as a prop.
    const metaResponse = {
      title: content?.title ?? '',
      description: content?.description ?? '',
      thumbnail: content?.thumbnail ?? '',
      gameId: content?.game?.[0]?._id ?? '',
    }
    const rawLcpImageUrl = extractFirstImageUrl(content?.contentBody)
    // Lexical content (JSON containing "root") renders raw <img>; Tiptap (HTML)
    // is routed through the Next optimizer by processArticleImages, so only the
    // HTML path should preload the optimized URL to avoid a preload/img mismatch.
    const isHtmlBody =
      typeof content?.contentBody === 'string' && !content.contentBody.includes('root')
    const lcpImageUrl = isHtmlBody ? toOptimizedLcpUrl(rawLcpImageUrl) : rawLcpImageUrl
    // Match processArticleImages' srcset so the browser preloads the exact
    // responsive candidate the rendered <img> selects.
    const canOptimizeLcp =
      isHtmlBody && rawLcpImageUrl && /^https?:\/\//i.test(rawLcpImageUrl) && !/\.gif(\?|#|$)/i.test(rawLcpImageUrl)
    const resolvedLcp = canOptimizeLcp ? (resolveImageSrc(rawLcpImageUrl as string) || (rawLcpImageUrl as string)) : null
    // Only our own media gets a srcset, because only our own media is rewritten
    // by processArticleImages. A third-party image keeps a single-URL preload, and
    // emitting candidates it will never match would just break the preload.
    const normalizedLcp = resolvedLcp && isMediaUrl(resolvedLcp) ? resolvedLcp : null
    const lcpImageSrcSet = normalizedLcp
      ? ARTICLE_BODY_WIDTHS.map((w) => `${mediaSrc(normalizedLcp, w)} ${w}w`).join(', ')
      : null

    // Intrinsic sizes for the body's own images, so the server HTML carries
    // width/height and nothing below an image moves when it loads. Capped: the
    // first few images are the ones above the fold; the rest are lazy anyway.
    // Failures leave gaps in the map, never fail the render.
    const bodyImageDimensions = isHtmlBody
      ? await probeImageDimensions(collectArticleImageUrls(content.contentBody as string).slice(0, 24))
      : {}

    // Share image (og:image / twitter:image): use the article's OWN thumbnail so
    // platforms like Facebook, Discord and X show the content image. Resolve it to
    // an absolute URL — crawlers can't fetch relative/protocol-relative paths, and
    // some thumbnails are stored as bare host/path. When the article has no
    // thumbnail we fall back (in the component) to the site's branded image.
    const rawThumbnail = metaResponse.thumbnail
    const resolvedThumbnail = resolveImageSrc(rawThumbnail)
    const ogImageUrl = resolvedThumbnail || null

    // ─── Metadata ─────────────────────────────────────────────────────────────
    const rawTitle: string = metaResponse.title || ''
    // Appending the brand only when it fits: Google renders ~60 characters, so on
    // a long Thai headline the suffix would just push real words out of the SERP.
    const pageTitle =
      rawTitle && rawTitle.length <= 55 ? `${rawTitle} | ${SITE_NAME}` : rawTitle
    // Fall back to the article's own opening text when an editor left the
    // description blank, so Google isn't left to invent a snippet.
    const bodyExcerpt = isHtmlBody ? stripHtmlToText(content?.contentBody as string) : ''
    const description = truncate(
      stripHtmlToText(metaResponse.description) || bodyExcerpt || rawTitle,
      300,
    )

    // NewsArticle structured data for Google rich results / Google News.
    const articleUrl = `${SITE_URL}/article/${articleId}`
    const authorName = content?.author?.[0]?.displayName
    const section = CATEGORY_SECTION[content?.category as string]
    const gameTitle = content?.game?.[0]?.title
    const gameId = metaResponse.gameId
    const jsonLd: Record<string, any>[] = []
    if (content) {
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
        url: articleUrl,
        headline: rawTitle,
        description,
        image: [resolvedThumbnail || SITE_SHARE_IMAGE],
        inLanguage: 'th',
        ...(section ? { articleSection: section.name } : {}),
        ...(Number(content.wordCount) > 0 ? { wordCount: Number(content.wordCount) } : {}),
        ...(content.publishDt ? { datePublished: content.publishDt } : {}),
        ...(content.updatedAt || content.publishDt
          ? { dateModified: content.updatedAt || content.publishDt }
          : {}),
        // Google requires an author; when no byline is set the publisher itself
        // is the honest answer rather than omitting the field.
        author: authorName
          ? { '@type': 'Person', name: authorName }
          : { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
        // Square brand logo — the 1200x630 share image was being sent here, and
        // Google rejects wide crops as a publisher logo.
        publisher: publisherLd,
        isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website` },
        ...(gameTitle && gameId
          ? {
              about: {
                '@type': 'VideoGame',
                name: gameTitle,
                url: `${SITE_URL}/game/${gameId}`,
              },
            }
          : {}),
      })
      jsonLd.push(
        breadcrumbLd([
          { name: 'หน้าแรก', path: '/' },
          ...(section ? [{ name: section.name, path: section.path }] : []),
          ...(gameTitle && gameId ? [{ name: gameTitle, path: `/game/${gameId}` }] : []),
          { name: rawTitle },
        ]),
      )
    }

    // Only the embedded game and author are slimmed: the populated game doc
    // (~8 KB) carries a gallery, links and full platform docs the article never
    // renders. Every other field passes through untouched for components/article.
    const initialArticle = content
      ? {
          ...content,
          game: (Array.isArray(content.game) ? content.game : []).map(slimArticleGame),
          author: (Array.isArray(content.author) ? content.author : []).map(slimArticleAuthor),
        }
      : null

    const [topBannerAd, topSquareAd, bottomBannerAd] = await bannerAdsPromise

    return {
      props: {
        articleId,
        metaResponse,
        initialArticle,
        pageTitle,
        description,
        lcpImageUrl,
        lcpImageSrcSet,
        ogImageUrl,
        jsonLd,
        bodyImageDimensions,
        topBannerAd,
        topSquareAd,
        bottomBannerAd,
      },
      revalidate: 1800,
    }
  } catch (err) {
    // A transient backend failure must not replace a good page with a 404
    // marker (OpenNext deletes the R2 key on notFound): throw so the stale ISR
    // copy keeps serving. Genuine 404s returned above never reach here.
    throw err
  }
}


